import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  Bars3Icon,
  DocumentDuplicateIcon,
  ClockIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, Select, Text } from '../components/ui'
import { usePermission } from '../hooks/usePermissions'
import { useUserLogs } from '../hooks/useUserLogs'
import { formsService } from '../services/formsService'
import { dashcorretorService } from '../services/dashcorretor'
import { BRAND_COLOR } from '../constants'
import type { Form, FormField, FormFieldTipo } from '../types/forms'
import type { DashCorretorClassificationRef } from '../types/dashcorretor'

const MODULE_NAME = 'portais_atendimento'

const FIELD_TYPE_OPTIONS: { value: FormFieldTipo; label: string }[] = [
  { value: 'text', label: 'Texto' },
  { value: 'textarea', label: 'Área de texto' },
  { value: 'number', label: 'Número' },
  { value: 'date', label: 'Data' },
  { value: 'select', label: 'Lista suspensa' },
  { value: 'radio', label: 'Opção única' },
  { value: 'checkbox', label: 'Múltipla escolha' },
]

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return 'Erro inesperado. Tente novamente.'
}

interface FormPreviewProps {
  form: Form
}

const FormPreview: React.FC<FormPreviewProps> = ({ form }) => {
  const [values, setValues] = useState<Record<string, string | string[]>>({})

  const setValue = (fieldId: number, value: string | string[]) => {
    setValues((prev) => ({ ...prev, [String(fieldId)]: value }))
  }

  const toggleCheckbox = (fieldId: number, optionValue: string) => {
    const key = String(fieldId)
    const current = Array.isArray(values[key]) ? (values[key] as string[]) : []
    const next = current.includes(optionValue)
      ? current.filter((v) => v !== optionValue)
      : [...current, optionValue]
    setValue(fieldId, next)
  }

  return (
    <div className="space-y-5">
      {form.schema.length === 0 ? (
        <Text size="sm" className="text-gray-500 dark:text-gray-400">
          Este formulário não possui campos.
        </Text>
      ) : (
        form.schema.map((field) => {
          const key = String(field.id)
          const value = values[key]

          return (
            <div key={field.id}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {field.label}
                {field.obrigatorio && (
                  <span className="text-red-500 dark:text-red-400 ml-0.5">*</span>
                )}
              </label>

              {field.tipo === 'textarea' ? (
                <textarea
                  value={typeof value === 'string' ? value : ''}
                  onChange={(e) => setValue(field.id, e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              ) : field.tipo === 'select' ? (
                <Select
                  value={typeof value === 'string' ? value : ''}
                  onChange={(e) => setValue(field.id, e.target.value)}
                  className="w-full"
                >
                  <option value="">Selecione...</option>
                  {(field.opcoes ?? []).map((opt) => (
                    <option key={opt.valor} value={opt.valor}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              ) : field.tipo === 'radio' ? (
                <div className="space-y-2">
                  {(field.opcoes ?? []).map((opt) => (
                    <label
                      key={opt.valor}
                      className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                    >
                      <input
                        type="radio"
                        name={`preview-${field.id}`}
                        checked={value === opt.valor}
                        onChange={() => setValue(field.id, opt.valor)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              ) : field.tipo === 'checkbox' ? (
                <div className="space-y-2">
                  {(field.opcoes ?? []).map((opt) => {
                    const selected = Array.isArray(value) && value.includes(opt.valor)
                    return (
                      <label
                        key={opt.valor}
                        className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleCheckbox(field.id, opt.valor)}
                        />
                        {opt.label}
                      </label>
                    )
                  })}
                </div>
              ) : (
                <Input
                  type={
                    field.tipo === 'number'
                      ? 'number'
                      : field.tipo === 'date'
                        ? 'date'
                        : 'text'
                  }
                  value={typeof value === 'string' ? value : ''}
                  onChange={(e) => setValue(field.id, e.target.value)}
                  className="w-full"
                />
              )}
            </div>
          )
        })
      )}
    </div>
  )
}

function nextFieldId(fields: FormField[]): number {
  const max = fields.reduce((acc, field) => Math.max(acc, field.id), 0)
  return max + 1
}

function createEmptyField(fields: FormField[]): FormField {
  return {
    id: nextFieldId(fields),
    tipo: 'text',
    label: '',
    obrigatorio: false,
  }
}

function cloneFields(fields: FormField[]): FormField[] {
  return fields.map((field) => ({
    ...field,
    opcoes: field.opcoes?.map((opt) => ({ ...opt })),
  }))
}

type BuilderMode = 'create' | 'version'

interface SortableFieldCardProps {
  field: FormField
  onChange: (field: FormField) => void
  onRemove: () => void
}

const SortableFieldCard: React.FC<SortableFieldCardProps> = ({
  field,
  onChange,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: String(field.id) })

  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    opacity: isDragging ? 0.7 : 1,
  }

  const needsOptions = field.tipo === 'select' || field.tipo === 'radio' || field.tipo === 'checkbox'

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4"
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          className="mt-2 p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-grab"
          title="Arrastar para reordenar"
          {...attributes}
          {...listeners}
        >
          <Bars3Icon className="h-5 w-5" />
        </button>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Tipo do campo
            </label>
            <Select
              value={field.tipo}
              onChange={(e) => {
                const tipo = e.target.value as FormFieldTipo
                const next: FormField = { ...field, tipo }
                if (
                  (tipo === 'select' || tipo === 'radio' || tipo === 'checkbox') &&
                  !next.opcoes?.length
                ) {
                  next.opcoes = [{ valor: '', label: '' }]
                }
                if (tipo !== 'select' && tipo !== 'radio' && tipo !== 'checkbox') {
                  delete next.opcoes
                }
                onChange(next)
              }}
              className="w-full"
            >
              {FIELD_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Nome do campo
            </label>
            <Input
              value={field.label}
              onChange={(e) => onChange({ ...field, label: e.target.value })}
              placeholder="Ex.: Endereço"
              className="w-full"
            />
          </div>

          <div className="flex items-end gap-2 pb-1">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={field.obrigatorio}
                onChange={(e) => onChange({ ...field, obrigatorio: e.target.checked })}
              />
              Campo obrigatório
            </label>
          </div>

          {needsOptions && (
            <div className="md:col-span-2 space-y-2">
              <Text size="xs" className="text-gray-600 dark:text-gray-300 font-medium">
                Opções da lista
              </Text>
              {(field.opcoes ?? []).map((opt, index) => (
                <div key={`${field.id}-opt-${index}`} className="flex gap-2">
                  <Input
                    value={opt.valor}
                    onChange={(e) => {
                      const opcoes = [...(field.opcoes ?? [])]
                      opcoes[index] = { ...opcoes[index], valor: e.target.value }
                      onChange({ ...field, opcoes })
                    }}
                    placeholder="Valor interno (ex.: casa)"
                    className="w-full"
                  />
                  <Input
                    value={opt.label}
                    onChange={(e) => {
                      const opcoes = [...(field.opcoes ?? [])]
                      opcoes[index] = { ...opcoes[index], label: e.target.value }
                      onChange({ ...field, opcoes })
                    }}
                    placeholder="Texto exibido (ex.: Casa)"
                    className="w-full"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const opcoes = (field.opcoes ?? []).filter((_, i) => i !== index)
                      onChange({ ...field, opcoes })
                    }}
                    className="p-2 text-red-600 dark:text-red-400"
                    title="Remover opção"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  onChange({
                    ...field,
                    opcoes: [...(field.opcoes ?? []), { valor: '', label: '' }],
                  })
                }
              >
                + Adicionar opção
              </Button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onRemove}
          className="mt-2 p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
          title="Remover campo"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}

const PortaisAtendimentoFormulariosTab: React.FC = () => {
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')
  const { logCreate, logUpdate, logDelete } = useUserLogs()

  const [forms, setForms] = useState<Form[]>([])
  const [classifications, setClassifications] = useState<DashCorretorClassificationRef[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const [builderOpen, setBuilderOpen] = useState(false)
  const [builderMode, setBuilderMode] = useState<BuilderMode>('create')
  const [builderTarget, setBuilderTarget] = useState<Form | null>(null)
  const [formName, setFormName] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set())
  const [fields, setFields] = useState<FormField[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const [metaOpen, setMetaOpen] = useState(false)
  const [metaTarget, setMetaTarget] = useState<Form | null>(null)
  const [metaName, setMetaName] = useState('')
  const [metaActive, setMetaActive] = useState(true)
  const [metaCategories, setMetaCategories] = useState<Set<string>>(new Set())

  const [versionsOpen, setVersionsOpen] = useState(false)
  const [versionsTarget, setVersionsTarget] = useState<Form | null>(null)
  const [versions, setVersions] = useState<Form[]>([])
  const [versionsLoading, setVersionsLoading] = useState(false)
  const [schemaPreview, setSchemaPreview] = useState<Form | null>(null)

  const [deleteTarget, setDeleteTarget] = useState<Form | null>(null)
  const [previewTarget, setPreviewTarget] = useState<Form | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  )

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [formList, refs] = await Promise.all([
        formsService.listFormGroups(),
        dashcorretorService.listClassificationReferences(),
      ])
      setForms(formList)
      setClassifications(refs)
    } catch (err) {
      setError(getErrorMessage(err))
      setForms([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const filteredForms = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return forms
    return forms.filter(
      (form) =>
        form.name.toLowerCase().includes(q) ||
        (form.categories ?? []).some((c) => c.toLowerCase().includes(q))
    )
  }, [forms, search])

  const resetBuilder = () => {
    setBuilderOpen(false)
    setBuilderMode('create')
    setBuilderTarget(null)
    setFormName('')
    setSelectedCategories(new Set())
    setFields([])
    setFormError(null)
    setIsSubmitting(false)
  }

  const openCreate = () => {
    if (!canInsert) return
    setBuilderMode('create')
    setBuilderTarget(null)
    setFormName('')
    setSelectedCategories(new Set())
    setFields([createEmptyField([])])
    setFormError(null)
    setBuilderOpen(true)
  }

  const openNewVersion = (form: Form) => {
    if (!canInsert && !canUpdate) return
    setBuilderMode('version')
    setBuilderTarget(form)
    setFormName(form.name)
    setSelectedCategories(new Set(form.categories ?? []))
    setFields(cloneFields(form.schema.length ? form.schema : [createEmptyField([])]))
    setFormError(null)
    setBuilderOpen(true)
  }

  const openMeta = (form: Form) => {
    if (!canUpdate) return
    setMetaTarget(form)
    setMetaName(form.name)
    setMetaActive(form.is_active)
    setMetaCategories(new Set(form.categories ?? []))
    setFormError(null)
    setMetaOpen(true)
  }

  const openVersions = async (form: Form) => {
    setVersionsTarget(form)
    setVersionsOpen(true)
    setVersionsLoading(true)
    setSchemaPreview(null)
    try {
      const list = await formsService.listVersions(form.form_group_id)
      setVersions(list)
    } catch (err) {
      setError(getErrorMessage(err))
      setVersions([])
    } finally {
      setVersionsLoading(false)
    }
  }

  const toggleCategory = (
    name: string,
    setter: React.Dispatch<React.SetStateAction<Set<string>>>
  ) => {
    setter((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const validateBuilder = (): string | null => {
    if (!formName.trim()) return 'Informe o nome do formulário.'
    if (fields.length === 0) return 'Adicione ao menos um campo.'
    for (const field of fields) {
      if (!field.label.trim()) return 'Todos os campos precisam de um nome.'
      if (
        (field.tipo === 'select' || field.tipo === 'radio' || field.tipo === 'checkbox') &&
        !(field.opcoes ?? []).some((o) => o.valor.trim() && o.label.trim())
      ) {
        return `O campo "${field.label || 'sem nome'}" precisa de opções válidas.`
      }
    }
    return null
  }

  const sanitizeFields = (list: FormField[]): FormField[] =>
    list.map((field) => {
      const next: FormField = {
        id: field.id,
        tipo: field.tipo,
        label: field.label.trim(),
        obrigatorio: field.obrigatorio,
      }
      if (field.tipo === 'select' || field.tipo === 'radio' || field.tipo === 'checkbox') {
        next.opcoes = (field.opcoes ?? [])
          .filter((o) => o.valor.trim() && o.label.trim())
          .map((o) => ({ valor: o.valor.trim(), label: o.label.trim() }))
      }
      return next
    })

  const handleBuilderSubmit = async () => {
    const validationError = validateBuilder()
    if (validationError) {
      setFormError(validationError)
      return
    }

    setIsSubmitting(true)
    setFormError(null)

    try {
      const schema = sanitizeFields(fields)
      const categories = Array.from(selectedCategories)

      if (builderMode === 'create') {
        const created = await formsService.createForm({
          name: formName,
          schema,
          categories,
          is_active: true,
        })
        void logCreate(
          'other',
          created.id,
          `Criou formulário: ${created.name}`,
          {
            form_group_id: created.form_group_id,
            version: created.version,
            action: 'create_form',
          }
        )
      } else if (builderTarget) {
        const created = await formsService.createNewVersion(builderTarget.form_group_id, {
          name: formName,
          schema,
          categories,
        })
        void logCreate(
          'other',
          created.id,
          `Criou versão ${created.version} do formulário: ${created.name}`,
          {
            form_group_id: created.form_group_id,
            version: created.version,
            action: 'create_form_version',
          }
        )
      }

      await loadData()
      resetBuilder()
    } catch (err) {
      setFormError(getErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleMetaSubmit = async () => {
    if (!metaTarget || !canUpdate) return
    if (!metaName.trim()) {
      setFormError('Informe o nome do formulário.')
      return
    }

    setIsSubmitting(true)
    setFormError(null)

    try {
      const oldValues = {
        name: metaTarget.name,
        is_active: metaTarget.is_active,
        categories: metaTarget.categories ?? [],
      }

      const updated = await formsService.updateFormMeta(metaTarget.id, {
        name: metaName,
        is_active: metaActive,
      })
      await formsService.updateAssignments(
        metaTarget.form_group_id,
        Array.from(metaCategories)
      )

      void logUpdate(
        'other',
        updated.id,
        `Atualizou metadados do formulário: ${updated.name}`,
        oldValues,
        {
          name: metaName.trim(),
          is_active: metaActive,
          categories: Array.from(metaCategories),
        },
        {
          form_group_id: metaTarget.form_group_id,
          action: 'update_form_meta',
        }
      )

      await loadData()
      setMetaOpen(false)
      setMetaTarget(null)
    } catch (err) {
      setFormError(getErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleActive = async (form: Form) => {
    if (!canUpdate) return
    try {
      const updated = await formsService.updateFormMeta(form.id, {
        is_active: !form.is_active,
      })
      void logUpdate(
        'other',
        updated.id,
        `${updated.is_active ? 'Ativou' : 'Desativou'} formulário: ${updated.name}`,
        { is_active: form.is_active },
        { is_active: updated.is_active },
        {
          form_group_id: form.form_group_id,
          action: 'toggle_form_status',
        }
      )
      await loadData()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget || !canDelete) return
    setIsSubmitting(true)
    try {
      await formsService.deleteFormGroup(deleteTarget.form_group_id)
      void logDelete(
        'other',
        deleteTarget.id,
        `Excluiu formulário: ${deleteTarget.name}`,
        {
          form_group_id: deleteTarget.form_group_id,
          action: 'delete_form',
        }
      )
      setDeleteTarget(null)
      await loadData()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setFields((prev) => {
      const oldIndex = prev.findIndex((f) => String(f.id) === String(active.id))
      const newIndex = prev.findIndex((f) => String(f.id) === String(over.id))
      if (oldIndex < 0 || newIndex < 0) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  const renderCategoryPicker = (
    selected: Set<string>,
    setter: React.Dispatch<React.SetStateAction<Set<string>>>
  ) => (
    <div className="flex flex-wrap gap-2">
      {classifications.length === 0 ? (
        <Text size="sm" className="text-gray-500 dark:text-gray-400">
          Nenhuma categoria cadastrada.
        </Text>
      ) : (
        classifications.map((ref) => {
          const active = selected.has(ref.name)
          return (
            <button
              key={ref.name}
              type="button"
              onClick={() => toggleCategory(ref.name, setter)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                active
                  ? 'border-transparent text-white'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800'
              }`}
              style={active ? { backgroundColor: ref.color || BRAND_COLOR } : undefined}
            >
              {ref.name}
            </button>
          )
        })
      )}
    </div>
  )

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
            Formulários
          </Heading>
          <Text size="sm" color="muted" className="mt-1">
            Monte formulários por categoria. Alterar campos gera uma nova versão.
          </Text>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => void loadData()}>
            Atualizar
          </Button>
          {canInsert && (
            <Button
              onClick={openCreate}
              className="text-white"
              style={{ backgroundColor: BRAND_COLOR }}
            >
              <PlusIcon className="h-5 w-5 mr-1 inline-block" />
              Novo Formulário
            </Button>
          )}
        </div>
      </div>

      <div className="mb-4">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome ou categoria"
          className="w-full max-w-md"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="text-red-600 dark:text-red-400">{error}</div>
      ) : filteredForms.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center bg-white dark:bg-gray-800">
          <Text size="sm" className="text-gray-700 dark:text-gray-300">
            Nenhum formulário cadastrado.
          </Text>
          {canInsert && (
            <Button
              onClick={openCreate}
              className="mt-4 text-white"
              style={{ backgroundColor: BRAND_COLOR }}
            >
              <PlusIcon className="h-5 w-5 inline-block mr-2" />
              Criar Primeiro Formulário
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/30">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Versão
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Categorias
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredForms.map((form) => (
                  <tr key={form.form_group_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                    <td className="px-4 py-4">
                      <Text size="sm" className="text-gray-900 dark:text-gray-100 font-medium">
                        {form.name}
                      </Text>
                      <Text size="xs" className="text-gray-500 dark:text-gray-400">
                        {form.schema.length} campo(s)
                      </Text>
                    </td>
                    <td className="px-4 py-4 text-center text-sm text-gray-700 dark:text-gray-300">
                      v{form.version}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(form.categories ?? []).length === 0 ? (
                          <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                        ) : (
                          (form.categories ?? []).map((cat) => (
                            <span
                              key={cat}
                              className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                            >
                              {cat}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-full ${
                          form.is_active
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-300'
                        }`}
                      >
                        {form.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setPreviewTarget(form)}
                          title="Visualizar"
                          className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => void openVersions(form)}
                          title="Ver versões"
                          className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <ClockIcon className="h-5 w-5" />
                        </button>
                        {canUpdate && (
                          <button
                            type="button"
                            onClick={() => openMeta(form)}
                            title="Editar meta"
                            className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                        )}
                        {(canInsert || canUpdate) && (
                          <button
                            type="button"
                            onClick={() => openNewVersion(form)}
                            title="Nova versão"
                            className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <DocumentDuplicateIcon className="h-5 w-5" />
                          </button>
                        )}
                        {canUpdate && (
                          <button
                            type="button"
                            onClick={() => void handleToggleActive(form)}
                            title={form.is_active ? 'Desativar' : 'Ativar'}
                            className="px-2 py-1 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            {form.is_active ? 'Desativar' : 'Ativar'}
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(form)}
                            title="Excluir"
                            className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal criar / nova versão */}
      {builderOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                {builderMode === 'create'
                  ? 'Novo Formulário'
                  : `Nova Versão (atual: v${builderTarget?.version ?? 1})`}
              </Heading>
              <Button variant="ghost" onClick={resetBuilder} disabled={isSubmitting}>
                <XMarkIcon className="h-6 w-6" />
              </Button>
            </div>

            {builderMode === 'version' && (
              <div className="mb-4 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2">
                <Text size="sm" className="text-amber-800 dark:text-amber-200">
                  A estrutura do formulário é imutável. Ao salvar, será criada a versão{' '}
                  {(builderTarget?.version ?? 1) + 1} e a anterior será desativada.
                </Text>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome
                </label>
                <Input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Ex.: Solicitação de Instalação"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Categorias
                </label>
                {renderCategoryPicker(selectedCategories, setSelectedCategories)}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Campos do Formulário
                </label>

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                  <SortableContext
                    items={fields.map((f) => String(f.id))}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {fields.map((field) => (
                        <SortableFieldCard
                          key={field.id}
                          field={field}
                          onChange={(next) =>
                            setFields((prev) =>
                              prev.map((f) => (f.id === field.id ? next : f))
                            )
                          }
                          onRemove={() =>
                            setFields((prev) => prev.filter((f) => f.id !== field.id))
                          }
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                <div className="mt-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFields((prev) => [...prev, createEmptyField(prev)])}
                  >
                    <PlusIcon className="h-4 w-4 mr-1 inline-block" />
                    Adicionar Campo
                  </Button>
                </div>
              </div>
            </div>

            {formError && (
              <div className="mt-4 text-red-600 dark:text-red-400 text-sm">{formError}</div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="ghost" onClick={resetBuilder} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                onClick={() => void handleBuilderSubmit()}
                disabled={isSubmitting}
                className="text-white"
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {isSubmitting
                  ? 'Salvando...'
                  : builderMode === 'create'
                    ? 'Criar Formulário'
                    : 'Criar Nova Versão'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal editar meta */}
      {metaOpen && metaTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                Editar Meta
              </Heading>
              <Button
                variant="ghost"
                onClick={() => {
                  if (!isSubmitting) {
                    setMetaOpen(false)
                    setMetaTarget(null)
                    setFormError(null)
                  }
                }}
              >
                <XMarkIcon className="h-6 w-6" />
              </Button>
            </div>

            <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-4">
              A estrutura dos campos não pode ser alterada aqui. Use &quot;Nova Versão&quot;.
            </Text>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome
                </label>
                <Input
                  value={metaName}
                  onChange={(e) => setMetaName(e.target.value)}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Categorias
                </label>
                {renderCategoryPicker(metaCategories, setMetaCategories)}
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={metaActive}
                  onChange={(e) => setMetaActive(e.target.checked)}
                />
                Formulário ativo
              </label>
            </div>

            {formError && (
              <div className="mt-4 text-red-600 dark:text-red-400 text-sm">{formError}</div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="ghost"
                disabled={isSubmitting}
                onClick={() => {
                  setMetaOpen(false)
                  setMetaTarget(null)
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => void handleMetaSubmit()}
                disabled={isSubmitting}
                className="text-white"
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal versões */}
      {versionsOpen && versionsTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                Versões — {versionsTarget.name}
              </Heading>
              <Button
                variant="ghost"
                onClick={() => {
                  setVersionsOpen(false)
                  setVersionsTarget(null)
                  setSchemaPreview(null)
                }}
              >
                <XMarkIcon className="h-6 w-6" />
              </Button>
            </div>

            {versionsLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-3">
                {versions.map((version) => (
                  <div
                    key={version.id}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 flex items-center justify-between gap-3"
                  >
                    <div>
                      <Text size="sm" className="text-gray-900 dark:text-gray-100 font-medium">
                        Versão {version.version}
                      </Text>
                      <Text size="xs" className="text-gray-500 dark:text-gray-400">
                        {new Date(version.created_at).toLocaleString('pt-BR')} ·{' '}
                        {version.schema.length} campo(s) ·{' '}
                        {version.is_active ? 'Ativa' : 'Inativa'}
                      </Text>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setSchemaPreview((prev) =>
                          prev?.id === version.id ? null : version
                        )
                      }
                    >
                      {schemaPreview?.id === version.id ? 'Ocultar' : 'Ver estrutura'}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {schemaPreview && (
              <pre className="mt-4 p-3 rounded-md bg-gray-50 dark:bg-gray-900 text-xs text-gray-800 dark:text-gray-200 overflow-x-auto border border-gray-200 dark:border-gray-700">
                {JSON.stringify(schemaPreview.schema, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* Modal visualizar (prévia — não grava) */}
      {previewTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-2">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                Visualização
              </Heading>
              <Button variant="ghost" onClick={() => setPreviewTarget(null)}>
                <XMarkIcon className="h-6 w-6" />
              </Button>
            </div>

            <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
              {previewTarget.name} · versão {previewTarget.version}
            </Text>
            <Text size="xs" className="text-amber-700 dark:text-amber-300 mb-5">
              Prévia apenas — nada é gravado. Assim o formulário aparece no portal de atendimento.
            </Text>

            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-5">
              <FormPreview key={previewTarget.id} form={previewTarget} />
            </div>

            <div className="mt-6 flex justify-end">
              <Button variant="ghost" onClick={() => setPreviewTarget(null)}>
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal exclusão */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md p-6">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
              Excluir formulário?
            </Heading>
            <Text size="sm" className="text-gray-600 dark:text-gray-300 mb-4">
              O formulário &quot;{deleteTarget.name}&quot; será excluído logicamente (todas as
              versões). As respostas já registradas permanecem intactas.
            </Text>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                disabled={isSubmitting}
                onClick={() => setDeleteTarget(null)}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => void handleDelete()}
                disabled={isSubmitting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {isSubmitting ? 'Excluindo...' : 'Excluir'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PortaisAtendimentoFormulariosTab
